import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { NeedsActivation } from "@/components/app/activation/needs-activation";
import { EmptyState } from "@/components/app/lists/empty-state";
import { PageHeader } from "@/components/app/shell/page-header";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { Skeleton } from "@/components/ui/skeleton";
import { useFacility } from "@/lib/facility/facility";
import { orpc } from "@/lib/orpc";
import { useCan } from "@/lib/policy";
import { guardRoute } from "@/lib/policy/route-guard";
import { FacilityPaymentCard } from "./-components/billing/facility-payment-card";
import { GatewayCard } from "./-components/billing/gateway-card";
import { RotessaCard } from "./-components/billing/rotessa-card";

export const Route = createFileRoute("/_authenticated/_administration/settings/payments")({
  beforeLoad: ({ context }) => guardRoute(context.me, "online-payments"),
  component: PaymentsSettings,
});

/** Per community: Stripe and Rotessa keys, then who takes cards and bank payments, then rules. Keys
 * need `financial:reconcile`; choices and rules are the community administrator's. */
function PaymentsSettings() {
  const { facilityId } = useFacility();
  const chosen = facilityId === "all" ? null : facilityId;
  const canConnect = useCan("financial:reconcile", chosen);

  const stripe = useQuery({
    ...orpc.financials.payments.gatewaySettings.get.queryOptions({
      input: { facilityId: chosen ?? undefined, provider: "stripe" },
    }),
    enabled: canConnect && chosen !== null,
  });
  const rotessa = useQuery({
    ...orpc.financials.payments.gatewaySettings.get.queryOptions({
      input: { facilityId: chosen ?? undefined, provider: "rotessa" },
    }),
    enabled: canConnect && chosen !== null,
  });

  if (!chosen) {
    return (
      <div className="flex flex-col gap-6">
        <PageHeader title="Online payments" meta="Keys and rules are set per community." />
        <EmptyState
          title="Choose a community"
          description="Each community connects its own Stripe and Rotessa accounts. Pick one in the community switcher above."
        />
      </div>
    );
  }

  const failed = stripe.isError ? stripe : rotessa.isError ? rotessa : null;
  return (
    <div className="flex flex-col gap-6">
      <PageHeader title="Online payments" meta="This community's processors and its own rules." />

      {!canConnect ? (
        <EmptyState
          title="The processor accounts are the finance office's"
          description="Connecting or changing Stripe or Rotessa needs the reconcile right."
        />
      ) : stripe.isPending || rotessa.isPending ? (
        <Skeleton className="h-96 w-full" />
      ) : failed || !stripe.data || !rotessa.data ? (
        <ReadFailed
          message="The processor accounts could not be loaded, so none are shown."
          error={failed?.error ?? null}
          retrying={stripe.isFetching || rotessa.isFetching}
          onRetry={() => {
            void stripe.refetch();
            void rotessa.refetch();
          }}
        />
      ) : (
        <>
          {stripe.data.account || rotessa.data.account ? null : (
            <NeedsActivation featureKey="payments" facilityId={chosen} />
          )}
          <GatewayCard key={`stripe-${chosen}`} facilityId={chosen} settings={stripe.data} />
          <RotessaCard key={`rotessa-${chosen}`} facilityId={chosen} settings={rotessa.data} />
        </>
      )}

      <FacilityPaymentCard facilityId={chosen} />
    </div>
  );
}
